<script lang="ts" setup>
import { toRefs, useTemplateRef, watch } from 'vue';
import { useFocus } from '@vueuse/core';
import VInputGroup from '../VInputGroup/VInputGroup.vue';
import VInputGroupAddon from '../VInputGroup/VInputGroupAddon.vue';

const props = withDefaults(
  defineProps<{
    // Express labelAria anders kwamen er conflicten met de bestaande aria-label prop
    labelAria?: string;
    path?: string;
    size?: 'm' | 'flexible';
    value?: string | null;
    name?: string;
    placeholder?: string;
    maxLength?: number;
    readonly?: boolean;
    disabled?: boolean;
    autocomplete?: boolean;
    hasError?: boolean;
    required?: boolean;
    id?: string;
    dataTestid?: string;
    focusOnMount?: boolean;
    inputClass?: string | string[] | object;
  }>(),
  {
    autocomplete: true,
    size: 'm',
  },
);

const emit = defineEmits<{
  keydown: [event: KeyboardEvent];
  updateField: [value: { path: string | undefined; value: string }];
  'update:value': [value: string | undefined];
  blur: [];
  keypress: [event: KeyboardEvent];
}>();

const modelValue = defineModel<string | null>();

const { focusOnMount } = toRefs(props);

const input = useTemplateRef('input');
const { focused } = useFocus(input, { initialValue: focusOnMount.value });

defineExpose({
  focus: () => (focused.value = true),
  select: () => input.value?.select(),
});

watch(
  () => props.value,
  (newVal) => {
    if (newVal) {
      modelValue.value = newVal;
    }
  },
  { immediate: true },
);

function onBlur(event: any) {
  handleInput(event);
  emit('blur');
}

function onEnter(event: any) {
  handleInput(event);
}

function handleInput(event: InputEvent) {
  const target = event.target as HTMLInputElement;
  let value = target.value.trim();

  if (props.maxLength && value.length > props.maxLength) {
    value = value.slice(0, props.maxLength);
  }

  if (value !== target.value) {
    target.value = value;
  }

  emit('updateField', { path: props.path, value });
  emit('update:value', modelValue.value || '');
}
</script>

<template>
  <VInputGroup class="group/stringField relative flex flex-1" :class="{ filled: !!modelValue }">
    <input
      v-if="!readonly"
      :aria-label="props.labelAria"
      type="text"
      ref="input"
      class="styled-input peer text-ellipsis group-[&>*:not(:first-child)]/input-group:rounded-s-none group-[&>*:not(:last-child)]/input-group:rounded-e-none group-[&>*:not(:first-child)]/input-group:border-s-0 group-[&>*:not(:last-child)]/input-group:border-e-0"
      :class="[
        {
          '!border-red-500 ': props.hasError,
          '!rounded-e-none !rounded-s-lg !border-r-0': !!props.maxLength,
          '!h-unset': props.size === 'flexible',
        },
        props.inputClass,
      ]"
      :id="id"
      :required="required"
      :autocomplete="autocomplete ? 'on' : 'off'"
      :name="name"
      :placeholder="placeholder"
      :disabled="disabled"
      :maxlength="props.maxLength"
      v-model="modelValue"
      @blur="onBlur"
      @keydown="(event) => emit('keydown', event)"
      @keyup.enter="onEnter"
      :data-testid="props.dataTestid || 'string-field-input'"
      @keypress="(e) => emit('keypress', e)"
    />
    <div v-else>
      <div
        class="filled whitespace-normal break-words group-[]/float-label:h-11 group-[]/float-label:content-center"
        data-testid="string-field-readonly-text"
      >
        {{ modelValue || '-' }}
      </div>
    </div>

    <VInputGroupAddon
      v-if="!readonly && !!props.maxLength"
      class="border-l-0"
      :class="{ 'border-red-500 ': props.hasError }"
    >
      <span class="group-[:not(:focus-within)]/stringField:!hidden">
        {{ modelValue?.length ?? 0 }}/{{ props.maxLength }}
      </span>
    </VInputGroupAddon>
  </VInputGroup>
</template>
